/**
 * 话题评分对话框组件
 * 当话题完成时显示，收集用户评分和反馈
 */

import React, { useState } from 'react';
import { Star, X, Trophy } from 'lucide-react';

interface TopicRatingDialogProps {
  isOpen: boolean;
  topicId: string;
  topicTitle?: string;
  completionReason?: string;
  onSubmit: (rating: number, feedback?: string) => void;
  onSkip: () => void;
  onClose: () => void;
}

export const TopicRatingDialog: React.FC<TopicRatingDialogProps> = ({
  isOpen,
  topicId,
  topicTitle,
  completionReason,
  onSubmit,
  onSkip,
  onClose
}) => {
  const [rating, setRating] = useState<number>(0);
  const [feedback, setFeedback] = useState<string>('');
  const [hoveredRating, setHoveredRating] = useState<number>(0);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (rating > 0) {
      onSubmit(rating, feedback.trim() || undefined);
    }
  };

  const handleSkip = () => {
    onSkip();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
        {/* 头部 */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Trophy className="w-6 h-6 text-yellow-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              恭喜！您已经完成了这个话题的学习
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 完成信息 */}
        <div className="mb-6">
          {topicTitle && (
            <p className="text-base font-medium text-gray-800 dark:text-gray-200 mb-2">
              话题：{topicTitle}
            </p>
          )}
          {completionReason && (
            <p className="text-sm text-gray-700 dark:text-gray-300 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
              {completionReason}
            </p>
          )}
        </div>

        {/* 评分区域 */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            请为这次学习体验评分：
          </label>
          <div className="flex items-center space-x-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                className="p-1 transition-colors"
              >
                <Star
                  className={`w-8 h-8 ${
                    star <= (hoveredRating || rating)
                      ? 'text-yellow-400 fill-current'
                      : 'text-gray-300 dark:text-gray-600'
                  }`}
                />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              {rating === 1 && '需要改进'}
              {rating === 2 && '一般'}
              {rating === 3 && '还不错'}
              {rating === 4 && '很好'}
              {rating === 5 && '非常棒！'}
            </p>
          )}
        </div>

        {/* 反馈区域 */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            反馈建议（可选）：
          </label>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="分享您的学习感受或建议..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-blue-500 focus:border-transparent
                     resize-none"
            rows={3}
            maxLength={500}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {feedback.length}/500
          </p>
        </div>

        {/* 按钮区域 */}
        <div className="flex space-x-3">
          <button
            onClick={handleSkip}
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 
                     rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
          >
            跳过
          </button>
          <button
            onClick={handleSubmit}
            disabled={rating === 0}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 
                     text-white rounded-lg transition-colors disabled:cursor-not-allowed"
          >
            提交评分
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopicRatingDialog;